/**
 * layerSymbology — single source of truth for how a layer is symbolized.
 *
 * Franco's principle: "whatever mechanism symbolizes should know." One
 * descriptor drives BOTH the Mapbox `match` expressions the renderer applies
 * AND the legend entries the widget/print render — so the map and the legend
 * can never disagree.
 *
 * Today only the fire layer is multi-symbol (Active vs DECLARED_OUT, from #622).
 * Any future categorical layer gets rendering + legend for free by returning a
 * descriptor here.
 */

// OUT fires render black + smaller than active fires. These are the canonical
// values; both the renderer and the legend read them from this descriptor.
export const OUT_FIRE_ICON_COLOR = '#000000';
export const OUT_FIRE_ICON_SIZE = 0.675;

/** #653 status colours (Mike Gravel): Being Monitored → yellow, Being Held / Under Control → green. */
export const FIRE_MONITORED_COLOR = '#ffc107';
// #653: dark green — chosen for low luminance so it separates clearly from the
// red (OC) and yellow (monitored) icons for red-green colour vision.
export const FIRE_HELD_COLOR = '#018a01';

/** Default active icon colour when a style omits icon-color (mirrors LiveGeoJSONLayer). */
const DEFAULT_ICON_COLOR = '#3b82f6';

export interface SymbologyCategory {
  /**
   * Property value(s) this category matches; a string[] maps several status
   * strings to one icon (Mapbox `match` accepts a labels array). null = the
   * default ("else") branch.
   */
  match: string | string[] | null;
  label: string;
  color: string;
  /** Mapbox icon-size for this category (also used to scale the legend swatch). */
  size: number;
  /**
   * #697: optional icon outline. Only the black "Out" icon needs a white halo
   * so it stays visible on dark backgrounds; active (coloured) fire icons carry
   * no outline. Absent = no halo.
   */
  haloColor?: string;
  haloWidth?: number;
  /**
   * Optional subtle drop shadow. Active fire icons get a grey shadow to lift
   * them off the map; the Out icon uses its white halo instead. Absent = none.
   */
  shadowColor?: string;
  shadowBlur?: number;
}

/** Subtle grey drop shadow for active fire icons (not the Out icon). */
const ACTIVE_FIRE_SHADOW_COLOR = '#111111';
const ACTIVE_FIRE_SHADOW_BLUR = 2;

export interface SymbologyDescriptor {
  iconName: string;
  matchProperty: string;
  /** Ordered; exactly one category has match === null (the default), placed last. */
  categories: SymbologyCategory[];
}

/** A normalized, render-agnostic legend entry. */
export interface LegendIconSymbol {
  kind: 'icon';
  iconName: string;
  color: string;
  size: number;
}
export interface SymbologyLegendEntry {
  label: string;
  symbol: LegendIconSymbol;
}

/**
 * Derive a multi-category symbology descriptor from a layer style, or null when
 * the layer is single-symbol (handled by the plain legend/render path).
 */
export function getSymbologyFromStyle(style: any): SymbologyDescriptor | null {
  if (!style || style.type !== 'symbol') return null;

  // Fire is the only categorical symbology today.
  if (style.icon === 'fire') {
    const activeColor = style.paint?.['icon-color'] || DEFAULT_ICON_COLOR;
    const activeSize = style.paint?.['icon-size'] || 1;
    return {
      iconName: 'fire',
      matchProperty: 'wildfireStatus',
      // #653: colour fire icons by status. Out of Control (and any unrecognized
      // status) is the red default/else branch — an unknown fire stays alarming,
      // never accidentally green. CLOSED + DECLARED_OUT group as black "Out".
      categories: [
        { match: 'BEING_MONITORED', label: 'Being Monitored', color: FIRE_MONITORED_COLOR, size: activeSize, shadowColor: ACTIVE_FIRE_SHADOW_COLOR, shadowBlur: ACTIVE_FIRE_SHADOW_BLUR },
        { match: ['BEING_HELD', 'UNDER_CONTROL'], label: 'Being Held / Under Control', color: FIRE_HELD_COLOR, size: activeSize, shadowColor: ACTIVE_FIRE_SHADOW_COLOR, shadowBlur: ACTIVE_FIRE_SHADOW_BLUR },
        { match: ['CLOSED', 'DECLARED_OUT'], label: 'Out', color: OUT_FIRE_ICON_COLOR, size: OUT_FIRE_ICON_SIZE, haloColor: '#ffffff', haloWidth: 2 },
        { match: null, label: 'Out of Control', color: activeColor, size: activeSize, shadowColor: ACTIVE_FIRE_SHADOW_COLOR, shadowBlur: ACTIVE_FIRE_SHADOW_BLUR },
      ],
    };
  }

  return null;
}

function defaultCategory(d: SymbologyDescriptor): SymbologyCategory {
  return d.categories.find((c) => c.match === null) ?? d.categories[d.categories.length - 1];
}

/**
 * Build the Mapbox `icon-image` match expression. `getIconId` maps a category to
 * the registered map image id for that category's icon.
 */
export function buildIconImageExpression(
  d: SymbologyDescriptor,
  getIconId: (category: SymbologyCategory) => string,
): any[] {
  const expr: any[] = ['match', ['get', d.matchProperty]];
  for (const cat of d.categories) {
    if (cat.match === null) continue;
    expr.push(cat.match, getIconId(cat));
  }
  expr.push(getIconId(defaultCategory(d)));
  return expr;
}

/** Build the Mapbox `icon-size` match expression from the category sizes. */
export function buildIconSizeExpression(d: SymbologyDescriptor): any[] {
  const expr: any[] = ['match', ['get', d.matchProperty]];
  for (const cat of d.categories) {
    if (cat.match === null) continue;
    expr.push(cat.match, cat.size);
  }
  expr.push(defaultCategory(d).size);
  return expr;
}

/**
 * Legend entries for a descriptor, default ("Active") first then the matched
 * categories — reading order for a legend. Render-agnostic (kind: 'icon').
 */
export function getSymbologyLegendEntries(d: SymbologyDescriptor): SymbologyLegendEntry[] {
  const ordered = [
    ...d.categories.filter((c) => c.match === null),
    ...d.categories.filter((c) => c.match !== null),
  ];
  return ordered.map((cat) => ({
    label: cat.label,
    symbol: { kind: 'icon', iconName: d.iconName, color: cat.color, size: cat.size },
  }));
}
